import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { useTranslation } from '@sshop/i18n';

import { RootErrorBoundary } from '../error/RootErrorBoundary';
import { NotFoundPage } from '../error/NotFoundPage';
import { RootLayout } from '@/widgets/RootLayout';
import { env } from '@/shared/config/env';
import { AppLoader } from '@/shared/ui/AppLoader';

import { featureRoutes } from './featureRoutes';
import { LocaleGuard, RequireAuth, RequireGuest, RequireCheckoutAuth } from './guards';
import { SearchRedirect } from './SearchRedirect';

const CHUNK_RELOAD_KEY = 'sshop:chunk-reload';
const CHUNK_LOAD_ERROR =
  /(?:failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|loading chunk \d+ failed)/i;
let chunkReloadPending = false;

function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_LOAD_ERROR.test(error.message);
}

function reloadForFreshChunks(): boolean {
  if (chunkReloadPending) return true;

  try {
    const attemptedPath = `${window.location.pathname}${window.location.search}`;
    if (window.sessionStorage.getItem(CHUNK_RELOAD_KEY) === attemptedPath) return false;
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, attemptedPath);
    chunkReloadPending = true;
    window.location.reload();
    return true;
  } catch {
    // Do not risk a reload loop when sessionStorage is unavailable.
    return false;
  }
}

export function registerChunkLoadRecovery() {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForFreshChunks()) event.preventDefault();
  });
}

/**
 * Lazy-load every page so each route becomes its own chunk. Page modules
 * re-export a named symbol; this helper adapts them to React.lazy's default
 * export contract.
 */
const lazyPage = <K extends string>(loader: () => Promise<Record<K, ComponentType>>, key: K) =>
  lazy(async () => {
    try {
      const module = await loader();
      try {
        window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      } catch {
        // Storage can be disabled independently of module loading.
      }
      return { default: module[key] };
    } catch (error) {
      if (isChunkLoadError(error) && reloadForFreshChunks()) {
        // Keep Suspense mounted while the hard refresh replaces this document.
        return new Promise<never>(() => undefined);
      }
      throw error;
    }
  });

const HomePage = lazyPage(() => import('@/pages/home'), 'HomePage');
const CategoriesPage = lazyPage(() => import('@/pages/categories'), 'CategoriesPage');
const CatalogPage = lazyPage(() => import('@/pages/catalog'), 'CatalogPage');
const ProductPage = lazyPage(() => import('@/pages/product'), 'ProductPage');
const ComparePage = lazyPage(() => import('@/pages/compare'), 'ComparePage');
const CartPage = lazyPage(() => import('@/pages/cart'), 'CartPage');
const FavoritesPage = lazyPage(() => import('@/pages/favorites'), 'FavoritesPage');
const CheckoutPage = lazyPage(() => import('@/pages/checkout'), 'CheckoutPage');
const OrderSuccessPage = lazyPage(() => import('@/pages/order-success'), 'OrderSuccessPage');
const AccountPage = lazyPage(() => import('@/pages/account'), 'AccountPage');
const LoginPage = lazyPage(() => import('@/pages/login'), 'LoginPage');
const RegisterPage = lazyPage(() => import('@/pages/register'), 'RegisterPage');
const ForgotPasswordPage = lazyPage(() => import('@/pages/forgot-password'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyPage(() => import('@/pages/reset-password'), 'ResetPasswordPage');
const AuthCallbackPage = lazyPage(() => import('@/pages/auth-callback'), 'AuthCallbackPage');
const AboutPage = lazyPage(() => import('@/pages/about'), 'AboutPage');
const ContactPage = lazyPage(() => import('@/pages/contact'), 'ContactPage');
const GuaranteePage = lazyPage(() => import('@/pages/guarantee'), 'GuaranteePage');
const ShippingPaymentPage = lazyPage(
  () => import('@/pages/shipping-payment'),
  'ShippingPaymentPage',
);
const NewsPage = lazyPage(() => import('@/pages/news'), 'NewsPage');
const NewsArticlePage = lazyPage(() => import('@/pages/news'), 'NewsArticlePage');
const PrivacyPolicyPage = lazyPage(() => import('@/pages/legal'), 'PrivacyPolicyPage');
const TermsConditionsPage = lazyPage(() => import('@/pages/legal'), 'TermsConditionsPage');

function RouteLoader() {
  const { t } = useTranslation();
  return <AppLoader label={t('app.loading')} />;
}

const withSuspense = (node: React.ReactNode) => (
  <Suspense fallback={<RouteLoader />}>{node}</Suspense>
);

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to={`/${env.DEFAULT_LOCALE}`} replace />,
  },
  {
    path: '/:locale',
    element: (
      <LocaleGuard>
        <RootLayout />
      </LocaleGuard>
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: withSuspense(<HomePage />) },
      { path: 'categories', element: withSuspense(<CategoriesPage />) },
      { path: 'catalog/:slug?', element: withSuspense(<CatalogPage />) },
      { path: 'search', element: <SearchRedirect /> },
      { path: 'product/:slug', element: withSuspense(<ProductPage />) },
      { path: 'compare', element: withSuspense(<ComparePage />) },
      { path: 'cart', element: withSuspense(<CartPage />) },
      { path: 'favorites', element: withSuspense(<FavoritesPage />) },
      { path: 'checkout', element: <RequireCheckoutAuth>{withSuspense(<CheckoutPage />)}</RequireCheckoutAuth> },
      { path: 'checkout/success/:orderId', element: withSuspense(<OrderSuccessPage />) },
      {
        path: 'login',
        element: <RequireGuest>{withSuspense(<LoginPage />)}</RequireGuest>,
      },
      {
        path: 'register',
        element: <RequireGuest>{withSuspense(<RegisterPage />)}</RequireGuest>,
      },
      {
        path: 'forgot-password',
        element: <RequireGuest>{withSuspense(<ForgotPasswordPage />)}</RequireGuest>,
      },
      {
        path: 'reset-password/:token',
        // Signed-in social accounts also use emailed tokens to set their first password.
        element: withSuspense(<ResetPasswordPage />),
      },
      {
        path: 'auth/social/callback',
        element: withSuspense(<AuthCallbackPage />),
      },
      {
        path: 'account/*',
        element: <RequireAuth>{withSuspense(<AccountPage />)}</RequireAuth>,
      },
      { path: 'about', element: withSuspense(<AboutPage />) },
      { path: 'contact', element: withSuspense(<ContactPage />) },
      { path: 'guarantee', element: withSuspense(<GuaranteePage />) },
      { path: 'shipping-payment', element: withSuspense(<ShippingPaymentPage />) },
      { path: 'news', element: withSuspense(<NewsPage />) },
      { path: 'news/:slug', element: withSuspense(<NewsArticlePage />) },
      { path: 'privacy-policy', element: withSuspense(<PrivacyPolicyPage />) },
      { path: 'terms-conditions', element: withSuspense(<TermsConditionsPage />) },
      // Feature modules contribute their own locale-scoped routes here.
      ...featureRoutes,
      { id: 'not-found', path: '*', element: <NotFoundPage /> },
    ],
  },
]);
